import React, { useEffect, useState } from "react";
import { useStore } from "./StoreContext";
import { useDocumentTitle } from "./StorefrontLayout";
import { toast } from "sonner";
import { LogOut, User as UserIcon, ShieldCheck, PawPrint, Wallet, Package, Receipt } from "lucide-react";

export default function Account() {
  useDocumentTitle("My account", "Sign in to track orders, save addresses and book pet grooming.");
  const { customer, logout, requestOtp, verifyOtp, fetchMe } = useStore();
  const [step, setStep] = useState("mobile");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (customer?.token && customer?.mobile) {
      fetchMe().then(setMe).catch(() => {});
    }
  }, [customer?.token, customer?.mobile]); // eslint-disable-line

  if (customer) {
    const pets = me?.pets || [];
    const orders = me?.orders || [];
    const invoices = me?.invoices || [];
    return (
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-12 space-y-5" data-testid="account-loggedin">
        {/* Header card */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 grid place-items-center text-amber-700"><UserIcon className="w-8 h-8" /></div>
          <h1 className="font-heading text-2xl font-black mt-3">Hi, {customer.name || "there"}!</h1>
          <div className="text-sm text-stone-500 font-mono mt-1">{customer.mobile}</div>
          <div className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1 font-semibold"><ShieldCheck className="w-3.5 h-3.5" />Verified via OTP</div>
          <button onClick={() => { logout(); toast.success("Signed out"); }} className="mt-5 inline-flex items-center gap-1.5 bg-stone-900 text-white px-5 py-2.5 rounded-full font-semibold text-sm" data-testid="account-logout">
            <LogOut className="w-4 h-4" />Sign out
          </button>
        </div>

        {/* Wallet */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-4" data-testid="account-wallet">
          <div className="w-12 h-12 rounded-full bg-amber-200 grid place-items-center text-amber-900"><Wallet className="w-6 h-6" /></div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Furmigo wallet</div>
            <div className="font-heading text-3xl font-black text-stone-900">₹{(customer.wallet_balance || me?.wallet_balance || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
            <div className="text-xs text-stone-500 mt-0.5">Use anywhere across our stores.</div>
          </div>
        </div>

        {/* Pets */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="account-pets">
          <div className="flex items-center gap-2 mb-3">
            <PawPrint className="w-5 h-5 text-amber-700" />
            <h2 className="font-heading text-xl font-black">Your pets</h2>
          </div>
          {pets.length === 0 ? (
            <div className="text-sm text-stone-500">No pets registered yet. Add them next time you visit our store.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {pets.map(p => (
                <div key={p.id} className="border border-stone-200 rounded-lg p-3" data-testid={`account-pet-${p.id}`}>
                  <div className="font-semibold text-sm">{p.name}</div>
                  <div className="text-xs text-stone-500">{p.species}{p.breed ? ` · ${p.breed}` : ""}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* POS Invoices (in-store purchases) */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="account-invoices">
          <div className="flex items-center gap-2 mb-3">
            <Receipt className="w-5 h-5 text-amber-700" />
            <h2 className="font-heading text-xl font-black">In-store bills</h2>
          </div>
          {invoices.length === 0 ? (
            <div className="text-sm text-stone-500">No in-store visits yet. We'll log every purchase here.</div>
          ) : (
            <div className="space-y-2">
              {invoices.slice(0, 10).map(inv => (
                <div key={inv.id} className="flex items-center justify-between border-b border-stone-100 pb-2" data-testid={`account-invoice-${inv.id}`}>
                  <div>
                    <div className="font-mono text-xs">{inv.invoice_no}</div>
                    <div className="text-[11px] text-stone-500">{new Date(inv.created_at).toLocaleDateString()} · {(inv.items || []).length} items</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-sm">₹{inv.grand_total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                    {inv.balance > 0 && <div className="text-[11px] text-rose-600">₹{inv.balance.toLocaleString("en-IN")} due</div>}
                    {inv.public_token && (
                      <a href={`/inv/${inv.id}?key=${inv.public_token}`} className="text-[11px] text-amber-700 hover:underline">View</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Storefront orders */}
        {orders.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="account-orders">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-5 h-5 text-amber-700" />
              <h2 className="font-heading text-xl font-black">Online orders</h2>
            </div>
            <div className="space-y-2">
              {orders.slice(0, 10).map(o => (
                <div key={o.id} className="flex items-center justify-between border-b border-stone-100 pb-2">
                  <div>
                    <div className="font-mono text-xs">{o.order_no}</div>
                    <div className="text-[11px] text-stone-500">{new Date(o.created_at).toLocaleDateString()} · {o.status}</div>
                  </div>
                  <div className="font-semibold text-sm">₹{(o.grand_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center text-xs text-stone-400">
          One Furmigo account works across all our stores nationwide.
        </div>
      </div>
    );
  }

  const send = async () => {
    if (!mobile) { toast.error("Enter mobile number"); return; }
    setBusy(true);
    try { await requestOtp(mobile); setStep("otp"); toast.success("OTP sent — check WhatsApp"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to send OTP"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    if (!code) { toast.error("Enter the OTP"); return; }
    setBusy(true);
    try { await verifyOtp(mobile, code, name); toast.success("Welcome to Furmigo!"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invalid OTP"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 pt-6" data-testid="account-login">
      <div className="bg-white border border-stone-200 rounded-2xl p-6">
        <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 grid place-items-center text-amber-700"><PawPrint className="w-8 h-8" /></div>
        <h1 className="font-heading text-2xl font-black text-center mt-3">Welcome to Furmigo</h1>
        <p className="text-sm text-stone-500 text-center mt-1">Sign in with your mobile to track orders & book grooming.</p>

        {step === "mobile" && (
          <div className="mt-5 space-y-3">
            <Field label="Your name" value={name} onChange={setName} testid="acc-name" />
            <Field label="Mobile number" value={mobile} onChange={setMobile} testid="acc-mobile" autoFocus />
            <button onClick={send} disabled={busy} className="w-full bg-stone-900 disabled:bg-stone-400 text-white py-3 rounded-full font-bold" data-testid="acc-send-otp">
              {busy ? "Sending…" : "Send OTP"}
            </button>
          </div>
        )}
        {step === "otp" && (
          <div className="mt-5 space-y-3">
            <div className="text-xs text-stone-500 text-center">OTP sent to {mobile}</div>
            <Field label="Enter 6-digit OTP" value={code} onChange={setCode} testid="acc-otp" autoFocus />
            <button onClick={verify} disabled={busy} className="w-full bg-stone-900 disabled:bg-stone-400 text-white py-3 rounded-full font-bold" data-testid="acc-verify">
              {busy ? "Verifying…" : "Verify & sign in"}
            </button>
            <button onClick={() => setStep("mobile")} className="w-full text-sm text-stone-500 hover:text-stone-900">Change number</button>
          </div>
        )}
      </div>
      <div className="text-center text-xs text-stone-400 mt-3">
        Your Furmigo account is shared across all our stores.
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testid, autoFocus }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{label}</label>
    <input value={value} onChange={e => onChange(e.target.value)} autoFocus={autoFocus} className="w-full mt-1 px-3 py-2.5 border border-stone-200 rounded-lg text-sm" data-testid={testid} />
  </div>
);
